import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { PanelLoader, useGetCrumbs } from "../../../../../features/ui"
import { useAddressLoaders } from "../../../../../features/address/loaders/addressLoaders"
import { operationHelpers } from "../../../../operations"
import { InvoiceSaleOperationForm } from "./invoiceSaleOperationForm"
import { Customer, SaleOperation, useCustomerLoaders } from "../../../../sales"
import { TableName } from "../../../../../tables"
import { Provincia } from "../../../../../services/gob-ar"
import { DeliveryOrder, useDeliveryOrderLoaders } from "../../../../deliveries"
import { FiscalDocument, useFiscalDocumentLoaders } from "../../../../fiscal-documents"
import { AdjustmentNote } from "../../../state/types"
import { useAdjustmentNoteLoaders } from "../../../loaders/adjustmentNoteLoaders"

export const InvoiceSaleOperationFormLoader = () => {
    const { tableName, dataType } = useRoutesHooks().parseUrl()
    const operation = dataType! as SaleOperation
    const [searchParams] = useSearchParams()
    const fiscalDocumentId = searchParams.get('fiscalDocumentId') ?? undefined

    const [customer, setCustomer] = useState<Customer>()
    const [provinces, setProvinces] = useState<Provincia[]>([])
    const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
    const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([])
    const [adjustmentNotes, setAdjustmentNotes] = useState<AdjustmentNote[]>([])
    const [ready, setReady] = useState(false)

    const { describeCode } = operationHelpers
    const customerLoaders = useCustomerLoaders()
    const addressLoaders = useAddressLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()
    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()

    const isSale = useMemo(() => tableName === TableName.SALE_ORDERS, [])
    const isHoard = useMemo(() => tableName === TableName.HOARD_ORDERS, [])

    useEffect(() => {
        const load = async () => {
            try {
                const { loadAllProvinces } = addressLoaders()
                const { loadCustomer } = customerLoaders()
                const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
                const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
                const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()

                // Comprobantes y notas se leen de la base: definen cuánto ajuste de precio queda por descontar
                const [loadedProvinces, loadedCustomer, loadedDeliveryOrders, loadedFiscalDocuments, loadedAdjustmentNotes] = await Promise.all([
                    loadAllProvinces(),
                    loadCustomer(operation.customerId),
                    isHoard ? loadOperationDeliveryOrders(operation.id) : Promise.resolve([]),
                    loadOperationFiscalDocuments(operation.id, false),
                    loadOperationAdjustmentNotes(operation.id, false)
                ])

                setProvinces(loadedProvinces)
                setCustomer(loadedCustomer)
                setDeliveryOrders(loadedDeliveryOrders)
                setFiscalDocuments(loadedFiscalDocuments)
                setAdjustmentNotes(loadedAdjustmentNotes)
            } finally {
                setReady(true)
            }
        }

        load()
    }, [])

    const title = useMemo(() => `Facturar ${isSale ? 'Venta' : 'Acopio'} ${describeCode(operation)}`, [operation])
    const crumbs = useGetCrumbs(
        isSale ? 'Ventas' : 'Acopios',
        isSale ? RoutePath.SALES : RoutePath.HOARDS,
        describeCode(operation),
        operation.id,
        'Facturar'
    )

    return (
        <PanelLoader
            title={title}
            crumbs={crumbs}
            ready={ready}
        >
            <InvoiceSaleOperationForm
                table={tableName!}
                operation={operation}
                customer={customer!}
                provinces={provinces}
                deliveryOrders={deliveryOrders}
                fiscalDocuments={fiscalDocuments}
                adjustmentNotes={adjustmentNotes}
                fiscalDocumentId={fiscalDocumentId}
            />
        </PanelLoader>
    )
}
